import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';

import { DataState } from '../model/data-state';
import { injectSchemaCondition } from '../model/schema-condition';
import { SchemaNode } from '../model/schema-node.types';
import { SchemaReference } from '../model/schema-reference.types';
import { isSchemaReference } from '../reference/compose-schema';
import { SchemaNodeScopeDirective } from './schema-node-scope.directive';
import { SchemaReferencePartDirective } from './schema-reference-part.directive';

@Component({
  selector: 'kros-schema-node-outlet',
  imports: [NgComponentOutlet, NgTemplateOutlet, SchemaNodeScopeDirective, SchemaReferencePartDirective],
  /**
   * The outlet is the node's gate (658386). It decides `showIf` and provides nothing itself, so the
   * condition is decided from outside the node — on the state the parent handed down, and on the source
   * *above* the node, which is what `$source` reaches from this injector. Everything the node is lives in
   * `SchemaNodeScopeDirective` inside, which exists only while the condition holds.
   *
   * The scope sits on an `ng-container`, so the component the node resolves to is still a direct child of
   * this element — the separators of `list-item-layout` select on exactly that.
   *
   * A node that stands for another document — a reference — resolves here once its `showIf` holds (670359), through a
   * `SchemaReferencePartDirective` the outlet creates for a reference only, so a plain node renders as it always did.
   * The place shows the reference's `$refStates.loading` while the part loads and its `$refStates.error` if the part
   * fails — nothing where the reference declares neither. The outlet never fetches a reference its `showIf` hides — a
   * preload one has been requested with its document already (672942) — and one hidden while it loads is aborted. Each
   * phase is a view of its own, so the part's scope never inherits the placeholder's. The phases' template is declared
   * inside the part's container, because a template's views take the
   * injector of where the template is declared: there they see the part's `SCHEMA_REFERENCE_CHAIN`. Whether to load the
   * part's root component before dropping the placeholder, closing the short blank between them, is decided later.
   */
  template: `
    @if (shownNode(); as node) {
      <ng-container
        #scope="krosSchemaNodeScope"
        [krosSchemaNodeScope]="node"
        [state]="state()"
      >
        @if (scope.component(); as resolved) {
          <ng-container *ngComponentOutlet="resolved; inputs: { config: scope.effectiveConfig(), state: scope.effectiveState() }" />
        }
      </ng-container>
    } @else if (shownReference(); as reference) {
      <ng-container
        #part="krosSchemaReferencePart"
        [krosSchemaReferencePart]="reference"
      >
        @switch (part.shown().phase) {
          @case ('loading') {
            <ng-container *ngTemplateOutlet="scoped; context: { $implicit: part.shown().node }" />
          }
          @case ('error') {
            <ng-container *ngTemplateOutlet="scoped; context: { $implicit: part.shown().node }" />
          }
          @default {
            <ng-container *ngTemplateOutlet="scoped; context: { $implicit: part.shown().node }" />
          }
        }

        <ng-template
          #scoped
          let-node
        >
          @if (node) {
            <ng-container
              #scope="krosSchemaNodeScope"
              [krosSchemaNodeScope]="node"
              [state]="state()"
            >
              @if (scope.component(); as resolved) {
                <ng-container *ngComponentOutlet="resolved; inputs: { config: scope.effectiveConfig(), state: scope.effectiveState() }" />
              }
            </ng-container>
          }
        </ng-template>
      </ng-container>
    }
  `,
  styles: `
    :host {
      display: contents;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchemaNodeOutletComponent {
  readonly node = input<SchemaNode | SchemaReference>();

  /** The state inherited from the parent. A node that declares its own `dataSource` overrides it. */
  readonly state = input<DataState>();

  /** Injected here, above the node's scope, so the condition's `$source` is the source the node lives in — never its own. */
  readonly #isSatisfied = injectSchemaCondition();

  readonly #isShown = computed(() => this.#isSatisfied(this.node()?.showIf, this.state()));

  /**
   * The node while it shows and is written out, rendered straight through its scope: the path every plain node takes.
   * One condition with the reference's below keeps it to a single view, as before references existed.
   */
  protected readonly shownNode = computed(() => {
    const node = this.node();

    return this.#isShown() && !isSchemaReference(node) ? node : undefined;
  });

  /** The node while it shows and stands for another document, which then loads through a part of its own. */
  protected readonly shownReference = computed(() => {
    const node = this.node();

    return this.#isShown() && isSchemaReference(node) ? node : undefined;
  });
}
